import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';
import { MessageCircle, X, Send, Mic, MicOff, Volume2, Bot } from 'lucide-react';

const ChatbotWidget = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { type: 'bot', text: i18n.language === 'sw' ? 'Habari! Ninaweza kukusaidiaje kuhusu mfumo wa Interlink?' : 'Hello! How can I help you with Interlink?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { scrollToBottom(); }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    const userMsg = { type: 'user', text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post('/chatbot/ask', { question: text, language: i18n.language });
      const botMsg = { type: 'bot', text: res.data.answer, title: res.data.title };
      setMessages(prev => [...prev, botMsg]);
      // Voice output
      speakText(res.data.answer);
    } catch (err) {
      setMessages(prev => [...prev, { type: 'bot', text: i18n.language === 'sw' ? 'Samahani, kuna tatizo. Jaribu tena.' : 'Sorry, there is an issue. Try again.' }]);
    }
    setLoading(false);
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = i18n.language === 'sw' ? 'sw-KE' : 'en-US';
      utterance.rate = 0.9;
      window.speechSynthesis.speak(utterance);
    }
  };

  const startListening = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert(i18n.language === 'sw' ? 'Kitambulisho cha sauti hakipo kwenye kivinjari chako.' : 'Speech recognition not available in your browser.');
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = i18n.language === 'sw' ? 'sw-KE' : 'en-US';
    recognition.interimResults = false;
    setListening(true);
    recognition.start();
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      setListening(false);
      sendMessage(transcript);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
  };

  const quickQuestions = i18n.language === 'sw' ? 
    ['Taarifa ya Mapato ni nini?', 'Jinsi ya kuongeza stock?', 'Faida inahesabiwaje?', 'Mizania ni nini?'] :
    ['What is Income Statement?', 'How to add stock?', 'How is profit calculated?', 'What is Balance Sheet?'];

  return (
    <>
      {/* Chat button */}
      {!open && (
        <button onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 p-4 bg-emerald-500 text-white rounded-full shadow-2xl hover:bg-emerald-600 transition animate-bounce">
          <MessageCircle size={24} />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[360px] max-w-[95vw] h-[500px] max-h-[80vh] bg-[#111a14] border border-white/10 rounded-2xl shadow-2xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/5 bg-emerald-500/10 rounded-t-2xl">
            <div className="flex items-center gap-2">
              <Bot size={20} className="text-emerald-400" />
              <div>
                <h3 className="text-white font-bold text-sm">Interlink Assistant</h3>
                <p className="text-gray-400 text-[10px]">{i18n.language === 'sw' ? 'Pata msaada kuhusu mfumo' : 'Get help about the system'}</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white"><X size={18} /></button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-3 rounded-xl text-sm ${
                  msg.type === 'user' ? 'bg-emerald-500 text-white rounded-br-md' : 'bg-white/5 text-gray-200 rounded-bl-md'
                }`}>
                  {msg.title && <p className="text-[10px] text-emerald-400 font-medium mb-1">{msg.title}</p>}
                  <p>{msg.text}</p>
                  {msg.type === 'bot' && (
                    <button onClick={() => speakText(msg.text)} className="mt-2 text-gray-400 hover:text-white">
                      <Volume2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/5 p-3 rounded-xl rounded-bl-md">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick questions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {quickQuestions.map((q, i) => (
                <button key={i} onClick={() => sendMessage(q)}
                  className="text-[10px] px-2.5 py-1.5 bg-white/5 border border-white/10 rounded-full text-gray-400 hover:text-white hover:border-white/20 transition">
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-white/5 flex gap-2">
            <button onClick={startListening}
              className={`p-2.5 rounded-xl transition ${listening ? 'bg-red-500 text-white animate-pulse' : 'bg-white/5 text-gray-400 hover:text-white'}`}>
              {listening ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
            <input type="text" value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage(input)}
              placeholder={i18n.language === 'sw' ? 'Andika swali lako...' : 'Type your question...'}
              className="flex-1 px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-emerald-500/50" />
            <button onClick={() => sendMessage(input)}
              className="p-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition">
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
